import styled from 'styled-components'

import { colors, spaces } from '@/config/global-parameters'
import { normalNumberFormatter } from '@/helpers/formatters'

export interface Option {
  key: string
  color: string
  count?: number
  ratio?: number
  order?: number
}

interface IChartBar {
  bordered?: boolean
  height?: string
  separatorBlend?: boolean
  textBlend?: boolean
}

interface IProgressBar {
  bordered?: boolean
  className?: string
  height?: string
  labeled?: boolean
  options?: Option[]
  separatorBlend?: boolean
  textBlend?: boolean
  total?: number
}

interface IColorFragment {
  color: string
}

interface ILabel {
  align?: string
  textBlend?: boolean
  separatorBlend?: boolean
}

export const ProgressBar = ({
  bordered = false,
  className,
  height = '20px',
  labeled = false,
  options,
  separatorBlend,
  textBlend,
  total = 1,
}: IProgressBar) => {
  return (
    <Wrapper className={className}>
      <ChartBar
        bordered={bordered}
        height={height}
        separatorBlend={separatorBlend}
        textBlend={textBlend}
      >
        <Bars>
          {options
            ?.sort(({ order: orderA }, { order: orderB }) => {
              if (orderA === undefined && orderB === undefined) {
                return 0
              } else if (!orderA) {
                return 1
              } else if (!orderB) {
                return -1
              } else {
                return orderA - orderB
              }
            })
            .map((op) => (
              <ColorFragment
                color={op.color}
                key={op.key + op.color}
                style={{ width: `${(op.ratio || (total ? (op.count || 0) / total : 0)) * 100}%` }}
              />
            ))}
        </Bars>
        <Labels>
          {options?.map(
            (op, idx) =>
              labeled && (
                <Label
                  align={idx ? 'right' : undefined}
                  key={op.key + op.color}
                  separatorBlend={separatorBlend}
                  textBlend={textBlend}
                >
                  {normalNumberFormatter.format(Number(op.count) || 0)}
                </Label>
              )
          )}
        </Labels>
        {separatorBlend && <Separator />}
      </ChartBar>
    </Wrapper>
  )
}

const Wrapper = styled.div`
  width: 100%;
  display: flex;
  background-color: white;
`

export const Bars = styled.div`
  display: flex;
  width: 100%;
  height: 100%;
`

const Labels = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  height: 100%;
  margin-left: -100%;
`

const ChartBar = styled.div<IChartBar>`
  display: flex;
  align-items: center;
  height: ${({ height }) => height};
  border: ${({ bordered }) => bordered && 'solid 1px'};
  background-color: ${colors.defaultBarColor};
  width: 100%;
`

export const Separator = styled.div`
  width: 2px;
  mix-blend-mode: difference;
  height: 100%;
  background-color: ${colors.colorWhite};
  margin-left: calc((50% - 1px) * -1);
`

const Label = styled.span<ILabel>`
  ${({ align }) => align}: 0;
  color: ${colors.colorWhite};
  font-size: 12px;
  padding: 0 4px;
  ${({ textBlend }) => textBlend && 'mix-blend-mode: difference;'}
  @media(max-width: ${spaces.tabletBreakpoint}) {
    display: none;
  }
`

export const ColorFragment = styled.div<IColorFragment>`
  display: flex;
  height: 100%;
  align-items: center;
  background-color: ${({ color }) => color};
`

export default ProgressBar
